import { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import MerchantContext from "../../../contexts/MerchantContext";
import { trpc } from "../../../trpc";
import { getEventTypeDescription, getEventTypeLabel } from "../shared/utils";
import {
  LEAD_EVENT_TYPES,
  RENTALS_TYPES,
  type LeadEventType,
  type RentalsType,
} from "../shared/types";

type SimiCatalogItem = {
  id: number;
  value: string;
};

type SimiCatalogs = {
  medio: SimiCatalogItem[];
  asesores: SimiCatalogItem[];
  estado: SimiCatalogItem[];
};

type SimiEventMapping = {
  event: LeadEventType;
  createLead?: {
    estadoId: number;
  };
};

type FormValues = {
  estadoId: string;
};

type RentalsTypeWithSimiConfig = {
  id: RentalsType;
  name: string;
  crmConfiguration?: {
    id: string;
    simiEventsMapping: SimiEventMapping[];
    isFallback?: boolean;
  };
};

const DEFAULT_FORM_VALUES: FormValues = {
  estadoId: "",
};

const SimiCrmConfiguration = (): JSX.Element => {
  const { merchantId, merchantType } = useContext(MerchantContext);

  const { data: crmConnection } = trpc.crmSelfConfig.get.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const {
    data: crmConfigurations,
    isLoading: isLoadingConfigurations,
  } = trpc.crmSelfConfig.listCrmConfigurations.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  const simiConfigurations = useMemo(
    () => (crmConfigurations || []).filter((config) => (config.provider as string) === "simi"),
    [crmConfigurations]
  );

  const fallbackConfiguration = useMemo(
    () => simiConfigurations.find((config: any) => config.isFallback),
    [simiConfigurations]
  );

  const rentalsTypesWithConfig = useMemo<RentalsTypeWithSimiConfig[]>(() => {
    if (merchantType !== "rentals") return [];

    return RENTALS_TYPES.map((rentalsType) => {
      const config =
        simiConfigurations.find((c: any) => c.rentalsType === rentalsType.id) ||
        fallbackConfiguration;

      return {
        id: rentalsType.id,
        name: rentalsType.name,
        crmConfiguration: config
          ? {
              id: config.id,
              simiEventsMapping: ((config as any).simiEventsMapping || []) as SimiEventMapping[],
              isFallback: !(config as any).rentalsType && (config as any).isFallback,
            }
          : undefined,
      };
    });
  }, [simiConfigurations, fallbackConfiguration, merchantType]);

  const [catalogs, setCatalogs] = useState<SimiCatalogs | null>(null);
  const [isLoadingCatalogs, setIsLoadingCatalogs] = useState(false);
  const [eventMappings, setEventMappings] = useState<SimiEventMapping[]>([]);
  const [formValuesByEvent, setFormValuesByEvent] = useState<Record<string, FormValues>>({});
  const [editingRentalsType, setEditingRentalsType] = useState<RentalsType | null>(null);

  const utils = trpc.useUtils();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getSimiCatalogs = (trpc.crmSelfConfig as any).getSimiCatalogs.useMutation({
    onSuccess: (result: any) => {
      if (result.success) {
        setCatalogs(result.data);
      } else {
        toast.error("Error", {
          description: result.error || "No se pudieron obtener los catalogos de Simi",
        });
      }
      setIsLoadingCatalogs(false);
    },
    onError: (error: any) => {
      toast.error("Error", {
        description: error.message || "Ocurrio un error al obtener los catalogos",
      });
      setIsLoadingCatalogs(false);
    },
  });

  const createConfiguration = trpc.crmSelfConfig.createCrmConfiguration.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.listCrmConfigurations.invalidate({ merchantId: merchantId || "" });
      toast.success("Configuracion guardada", {
        description: "La configuracion de eventos se ha guardado correctamente.",
      });
      setEditingRentalsType(null);
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrio un error al guardar la configuracion.",
      });
    },
  });

  const updateConfiguration = trpc.crmSelfConfig.updateCrmConfiguration.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.listCrmConfigurations.invalidate({ merchantId: merchantId || "" });
      toast.success("Configuracion actualizada", {
        description: "La configuracion de eventos se ha actualizado correctamente.",
      });
      setEditingRentalsType(null);
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrio un error al actualizar la configuracion.",
      });
    },
  });

  // Load catalogs when connection is available
  const simiClientId = (crmConnection as any)?.simi?.clientId;
  const simiClientSecret = (crmConnection as any)?.simi?.clientSecret;
  const simiScope = (crmConnection as any)?.simi?.scope;
  const simiInmobiliaria = (crmConnection as any)?.simi?.inmobiliaria;
  const simiAccessToken = (crmConnection as any)?.simi?.accessToken;

  useEffect(() => {
    if (
      simiClientId &&
      simiClientSecret &&
      simiScope &&
      simiInmobiliaria &&
      !catalogs &&
      !isLoadingCatalogs
    ) {
      setIsLoadingCatalogs(true);
      getSimiCatalogs.mutate({
        clientId: simiClientId,
        clientSecret: simiClientSecret,
        scope: simiScope,
        inmobiliaria: simiInmobiliaria,
        accessToken: simiAccessToken,
      });
    }
  }, [simiClientId, simiClientSecret, simiScope, simiInmobiliaria]);

  // Load event mappings when editing a rentals type
  useEffect(() => {
    if (!editingRentalsType) return;

    const typeConfig = rentalsTypesWithConfig.find((rt) => rt.id === editingRentalsType);
    const mappings = typeConfig?.crmConfiguration?.simiEventsMapping || [];

    setEventMappings(mappings);

    const formValues: Record<string, FormValues> = {};
    for (const mapping of mappings) {
      formValues[mapping.event] = {
        estadoId: mapping.createLead?.estadoId?.toString() || "",
      };
    }
    setFormValuesByEvent(formValues);
  }, [editingRentalsType, rentalsTypesWithConfig]);

  const getAvailableEventTypes = (): LeadEventType[] => {
    const usedEvents = eventMappings.map((m) => m.event);
    return LEAD_EVENT_TYPES.filter((e) => !usedEvents.includes(e));
  };

  const handleAddEventType = (eventType: LeadEventType) => {
    setEventMappings((prev) => [...prev, { event: eventType }]);
    setFormValuesByEvent((prev) => ({
      ...prev,
      [eventType]: { ...DEFAULT_FORM_VALUES },
    }));
  };

  const handleRemoveEventType = (eventType: LeadEventType) => {
    if (!merchantId || !editingRentalsType) return;

    const remainingMappings = eventMappings.filter((m) => m.event !== eventType);
    const remainingFormValues = { ...formValuesByEvent };
    delete remainingFormValues[eventType];

    setEventMappings(remainingMappings);
    setFormValuesByEvent(remainingFormValues);

    // Persist the deletion immediately
    const typeConfig = rentalsTypesWithConfig.find((rt) => rt.id === editingRentalsType);
    if (typeConfig?.crmConfiguration && !typeConfig.crmConfiguration.isFallback) {
      const simiEventsMapping: SimiEventMapping[] = remainingMappings.map((mapping) => {
        if (!mapping.createLead) return { event: mapping.event };
        const form = remainingFormValues[mapping.event];
        return {
          event: mapping.event,
          createLead: {
            estadoId: Number(form?.estadoId || mapping.createLead.estadoId),
          },
        };
      });
      updateConfiguration.mutate({
        merchantId,
        id: typeConfig.crmConfiguration.id,
        simiEventsMapping,
      } as any);
    }
  };

  const handleToggleCreateLead = (eventType: LeadEventType, checked: boolean) => {
    setEventMappings((prev) =>
      prev.map((m) => {
        if (m.event !== eventType) return m;
        if (checked) {
          return { ...m, createLead: { estadoId: 0 } };
        }
        const { createLead: _, ...rest } = m;
        return rest;
      })
    );
  };

  const handleInputChange = (eventType: string, field: keyof FormValues, value: string) => {
    setFormValuesByEvent((prev) => ({
      ...prev,
      [eventType]: {
        ...(prev[eventType] || DEFAULT_FORM_VALUES),
        [field]: value,
      },
    }));
  };

  const handleSave = () => {
    if (!merchantId || !editingRentalsType) return;

    // Validate all createLead mappings have required fields
    for (const mapping of eventMappings) {
      if (mapping.createLead) {
        const form = formValuesByEvent[mapping.event];
        if (!form?.estadoId) {
          toast.warning("Campos requeridos", {
            description: `El evento "${getEventTypeLabel(mapping.event)}" requiere Estado.`,
          });
          return;
        }
      }
    }

    const simiEventsMapping: SimiEventMapping[] = eventMappings.map((mapping) => {
      if (!mapping.createLead) return { event: mapping.event };

      const form = formValuesByEvent[mapping.event];
      return {
        event: mapping.event,
        createLead: {
          estadoId: Number(form.estadoId),
        },
      };
    });

    const typeConfig = rentalsTypesWithConfig.find((rt) => rt.id === editingRentalsType);

    if (typeConfig?.crmConfiguration && !typeConfig.crmConfiguration.isFallback) {
      updateConfiguration.mutate({
        merchantId,
        id: typeConfig.crmConfiguration.id,
        simiEventsMapping,
      } as any);
    } else {
      createConfiguration.mutate({
        merchantId,
        provider: "simi",
        rentalsType: editingRentalsType,
        simiEventsMapping,
      } as any);
    }
  };

  if (merchantType !== "rentals") return <></>;

  const availableEvents = getAvailableEventTypes();

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex flex-col gap-4">
        <div>
          <p className="text-lg font-bold mb-2">Acciones Simi</p>
          <p className="text-sm text-gray-600">
            Configura como Palomma debe crear clientes en Simi cuando se produzcan eventos.
          </p>
        </div>

        <Separator />

        {isLoadingConfigurations ? (
          <div className="flex flex-row gap-2">
            <Spinner size="sm" />
            <p className="text-sm text-gray-500">Cargando configuraciones...</p>
          </div>
        ) : !editingRentalsType ? (
          <>
            <div>
              <p className="text-sm font-semibold mb-2">Configuracion por Tipo de Negocio</p>
              <p className="text-xs text-gray-500 mb-4">
                Selecciona el tipo de negocio para el que deseas configurar el mapeo de eventos.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              {rentalsTypesWithConfig.map((rt) => {
                const configuredEvents = rt.crmConfiguration?.simiEventsMapping?.filter(
                  (m) => m.createLead
                );
                const hasConfig = configuredEvents && configuredEvents.length > 0;

                return (
                  <div
                    key={rt.id}
                    className="flex items-center justify-between rounded-lg border border-gray-200 p-4"
                  >
                    <div>
                      <p className="text-sm font-semibold">{rt.name}</p>
                      <p className="text-xs text-gray-500">
                        {hasConfig
                          ? configuredEvents
                              .map((m) => getEventTypeLabel(m.event))
                              .join(", ")
                          : "Sin eventos configurados"}
                      </p>
                    </div>
                    <div className="flex flex-row gap-2">
                      {hasConfig ? (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                          Configurado
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-0.5 text-xs font-semibold text-orange-700">
                          Pendiente
                        </span>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingRentalsType(rt.id)}
                      >
                        {hasConfig ? "Editar" : "Configurar"}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold">
                {rentalsTypesWithConfig.find((rt) => rt.id === editingRentalsType)?.name}
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setEditingRentalsType(null)}
              >
                Volver
              </Button>
            </div>

            <Separator />

            {/* Available events to add */}
            {availableEvents.length > 0 && (
              <div>
                <Label className="text-xs font-semibold text-gray-600 mb-2 block">
                  Agregar evento
                </Label>
                <div className="flex flex-wrap gap-2">
                  {availableEvents.map((eventType) => (
                    <TooltipProvider key={eventType}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAddEventType(eventType)}
                            className="text-xs"
                          >
                            <Plus className="size-3 mr-1" />
                            {getEventTypeLabel(eventType)}
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p className="text-xs max-w-64">
                            {getEventTypeDescription(eventType)}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ))}
                </div>
              </div>
            )}

            {/* Event mappings */}
            {eventMappings.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">
                No hay eventos configurados. Agrega un evento para comenzar.
              </p>
            ) : (
              <div className="flex flex-col gap-4">
                {eventMappings.map((mapping) => {
                  const form = formValuesByEvent[mapping.event] || DEFAULT_FORM_VALUES;

                  return (
                    <div
                      key={mapping.event}
                      className="rounded-lg border border-gray-200 p-4"
                    >
                      <div className="flex items-center justify-between mb-3">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <p className="text-sm font-semibold cursor-help">
                                {getEventTypeLabel(mapping.event)}
                              </p>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="text-xs max-w-64">
                                {getEventTypeDescription(mapping.event)}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveEventType(mapping.event)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>

                      <div className="flex items-center gap-2 mb-3">
                        <Checkbox
                          checked={!!mapping.createLead}
                          onCheckedChange={(checked) =>
                            handleToggleCreateLead(mapping.event, !!checked)
                          }
                        />
                        <Label className="text-sm text-gray-700">
                          Crear Lead
                        </Label>
                      </div>

                      {mapping.createLead && (
                        <div className="flex flex-col gap-3 ml-6">
                          {isLoadingCatalogs ? (
                            <div className="flex flex-row gap-2">
                              <Spinner size="sm" />
                              <p className="text-xs text-gray-500">Cargando catalogos...</p>
                            </div>
                          ) : !catalogs ? (
                            <p className="text-xs text-gray-500">
                              No se pudieron cargar los catalogos.
                            </p>
                          ) : (
                            <div>
                              <Label className="text-xs font-semibold text-gray-600">
                                Estado
                              </Label>
                              <select
                                value={form.estadoId}
                                onChange={(e) =>
                                  handleInputChange(mapping.event, "estadoId", e.target.value)
                                }
                                className="w-full rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm hover:border-gray-300 focus:border-purple-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-purple-500"
                              >
                                <option value="">Selecciona un estado</option>
                                {catalogs.estado.map((item) => (
                                  <option key={item.id} value={item.id}>
                                    {item.value}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <Separator />

            <div className="flex flex-row gap-2">
              <Button
                variant="main"
                onClick={handleSave}
                disabled={
                  createConfiguration.isLoading ||
                  updateConfiguration.isLoading ||
                  eventMappings.length === 0
                }
              >
                {(createConfiguration.isLoading || updateConfiguration.isLoading) && (
                  <Spinner size="sm" />
                )}
                Guardar
              </Button>
              <Button variant="ghost" onClick={() => setEditingRentalsType(null)}>
                Cancelar
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SimiCrmConfiguration;
